import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Send, Save, Hash, Loader2 } from "lucide-react";

/**
 * Admin config for the OAuth-button Discord verification flow: pick the #verify
 * channel, the Verified role, and (optionally) an Unverified role to strip, then
 * post the "✅ Verify Here" button into the channel.
 */
export default function VerifySetup() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [posting, setPosting] = useState(false);
  const [metaErr, setMetaErr] = useState("");
  const [channels, setChannels] = useState([]);
  const [roles, setRoles] = useState([]);
  const [cfg, setCfg] = useState({ verify_channel_id: "", verified_role_id: "", unverified_role_id: "" });

  const load = async () => {
    try {
      const s = await api.get("/admin/site-settings");
      const d = s.data || {};
      setCfg({
        verify_channel_id: d.verify_channel_id || "",
        verified_role_id: d.verified_role_id || "",
        unverified_role_id: d.unverified_role_id || "",
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load settings");
    }
    try {
      const [ch, rl] = await Promise.all([
        api.get("/admin/discord/channels"),
        api.get("/admin/discord/roles"),
      ]);
      setChannels(ch.data?.text || []);
      setRoles(rl.data?.roles || []);
    } catch (e) {
      setMetaErr(formatApiError(e.response?.data?.detail) || "Could not load Discord channels/roles");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site-settings", cfg);
      toast.success("Verification settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const postButton = async () => {
    setPosting(true);
    try {
      const { data } = await api.post("/admin/verify/post-button");
      toast.success(`Verify button posted to the channel`, { description: data.url });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to post button");
    } finally { setPosting(false); }
  };

  const selectCls = "w-full px-4 py-3 text-sm bg-[var(--ff-bg)] border border-[var(--ff-border)] rounded-md text-white";

  return (
    <div className="surface-card p-6" data-testid="verify-setup-card">
      <div className="label-eyebrow text-[var(--ff-red)] mb-2 flex items-center gap-2">
        <ShieldCheck size={12} /> ▮ DISCORD VERIFICATION · OAUTH BUTTON
      </div>
      <p className="text-xs text-[var(--ff-text-dim)] max-w-2xl mb-5">
        Post a <b className="text-white">✅ Verify Here</b> button in your #verify channel. Members click it, authorize with
        Discord, and the bot instantly grants the <b className="text-white">Verified</b> role (and removes the Unverified
        role, if set). The bot needs <b className="text-white">Manage Roles</b> and its own role above these two.
      </p>

      {loading ? (
        <div className="text-[var(--ff-text-dim)] font-mono-ui text-sm">Loading…</div>
      ) : (
        <div className="space-y-5">
          {metaErr && (
            <div className="text-amber-400 text-xs font-mono-ui border border-amber-400/30 rounded-md px-3 py-2" data-testid="verify-setup-meta-error">
              {metaErr}
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <div className="label-eyebrow mb-2 flex items-center gap-1"><Hash size={11} /> VERIFY CHANNEL</div>
              <select value={cfg.verify_channel_id} onChange={(e) => set("verify_channel_id", e.target.value)} className={selectCls} data-testid="verify-channel-select">
                <option value="">— none —</option>
                {channels.map((c) => <option key={c.id} value={c.id}>#{c.name}</option>)}
              </select>
            </div>
            <div>
              <div className="label-eyebrow mb-2 text-emerald-400">VERIFIED ROLE</div>
              <select value={cfg.verified_role_id} onChange={(e) => set("verified_role_id", e.target.value)} className={selectCls} data-testid="verified-role-select">
                <option value="">— none —</option>
                {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <div>
              <div className="label-eyebrow mb-2 text-[var(--ff-text-dim)]">UNVERIFIED ROLE (optional)</div>
              <select value={cfg.unverified_role_id} onChange={(e) => set("unverified_role_id", e.target.value)} className={selectCls} data-testid="unverified-role-select">
                <option value="">— none (don&apos;t remove any) —</option>
                {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
          </div>

          <div className="flex flex-wrap justify-end items-center gap-2 pt-1">
            <button
              onClick={postButton}
              disabled={posting || !cfg.verify_channel_id || !cfg.verified_role_id}
              className="btn-ghost text-xs border border-[var(--ff-border)] disabled:opacity-40"
              data-testid="verify-post-button-btn"
              title={!cfg.verify_channel_id || !cfg.verified_role_id ? "Pick a channel + Verified role, then Save first" : ""}
            >
              {posting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
              {posting ? "Posting…" : "Post Verify Button"}
            </button>
            <button onClick={save} disabled={saving} className="btn-primary text-xs" data-testid="verify-save-btn">
              <Save size={13} /> {saving ? "Saving…" : "Save Settings"}
            </button>
          </div>
          <p className="text-[0.65rem] font-mono-ui text-[var(--ff-text-dim)] uppercase tracking-[0.15em] text-right">
            Save before posting the button
          </p>
        </div>
      )}
    </div>
  );
}
